import type {
  ApiResponse,
  AuthResponse,
  AvailabilityResponse,
  ChangePasswordRequest,
  CheckoutRequest,
  CheckoutResponse,
  GuestbookEntry,
  GuestbookListResponse,
  GuestbookSubmitRequest,
  HealthResponse,
  IsoDate,
  PublicSettings,
  PublicUser,
  Quote,
  QuoteRequest,
  RefreshResponse,
  RoomTypeDetail,
  RoomsListResponse,
  RsvpRequest,
  RsvpResponse,
} from '@brandonstrong/types';
import { adminApi } from './adminApi';

export const API_URL: string = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

export class ApiClientError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: Record<string, string[]>;

  constructor(status: number, code: string, message: string, details?: Record<string, string[]>) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/** In-memory admin access token (never persisted to storage). */
let accessToken: string | null = null;
export const setAccessToken = (token: string | null): void => {
  accessToken = token;
};
export const getAccessToken = (): string | null => accessToken;

export interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Send the httpOnly refresh cookie (auth endpoints). */
  auth?: boolean;
  /** Attach the bearer access token (admin endpoints). */
  bearer?: boolean;
  /** Idempotency key for mutations that must not run twice (checkout). */
  idempotencyKey?: string;
  signal?: AbortSignal;
}

export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {};
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (options.bearer && accessToken) headers.Authorization = `Bearer ${accessToken}`;
  if (options.idempotencyKey) headers['Idempotency-Key'] = options.idempotencyKey;

  const res = await fetch(`${API_URL}${path}`, {
    method: options.method ?? 'GET',
    headers,
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    credentials: options.auth ? 'include' : 'same-origin',
    signal: options.signal,
  });

  const payload = (await res.json().catch(() => null)) as ApiResponse<T> | null;
  if (!payload) throw new ApiClientError(res.status, 'network_error', 'Empty or invalid response');
  if (!payload.ok) {
    throw new ApiClientError(
      res.status,
      payload.error.code,
      payload.error.message,
      payload.error.details,
    );
  }
  return payload.data;
}

/** Query-string builder that drops empty values. */
export const qs = (
  params: Record<string, string | number | boolean | undefined | null>,
): string => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : '';
};

export type StayWindow = { checkIn: IsoDate; checkOut: IsoDate } | null | undefined;
const windowQs = (w: StayWindow) => (w ? qs({ checkIn: w.checkIn, checkOut: w.checkOut }) : '');

export const api = {
  health: async (signal?: AbortSignal): Promise<HealthResponse> => {
    const res = await fetch(`${API_URL}/health`, { signal });
    if (!res.ok) throw new ApiClientError(res.status, 'unhealthy', 'API is not healthy');
    return (await res.json()) as HealthResponse;
  },
  settings: {
    public: (signal?: AbortSignal) => request<PublicSettings>('/v1/settings/public', { signal }),
  },
  rooms: {
    list: (window?: StayWindow, signal?: AbortSignal) =>
      request<RoomsListResponse>(`/v1/rooms${windowQs(window)}`, { signal }),
    detail: (slug: string, window?: StayWindow, signal?: AbortSignal) =>
      request<RoomTypeDetail>(`/v1/rooms/${slug}${windowQs(window)}`, { signal }),
    availability: (
      slug: string,
      params: { checkIn: IsoDate; checkOut: IsoDate; adults: number; children: number },
    ) => request<AvailabilityResponse>(`/v1/rooms/${slug}/availability${qs(params)}`),
  },
  quote: (body: QuoteRequest, signal?: AbortSignal) =>
    request<Quote>('/v1/quote', { method: 'POST', body, signal }),
  checkout: (body: CheckoutRequest, idempotencyKey: string) =>
    request<CheckoutResponse>('/v1/checkout', { method: 'POST', body, idempotencyKey }),
  reservation: (code: string, email: string) =>
    request<CheckoutResponse>(`/v1/reservations/${encodeURIComponent(code)}${qs({ email })}`),
  rsvp: {
    submit: (body: RsvpRequest, idempotencyKey: string) =>
      request<RsvpResponse>('/v1/rsvp', { method: 'POST', body, idempotencyKey }),
    lookup: (code: string, email: string) =>
      request<RsvpResponse>(`/v1/rsvp/${encodeURIComponent(code)}${qs({ email })}`),
  },
  guestbook: {
    list: (page = 1, pageSize = 20, signal?: AbortSignal) =>
      request<GuestbookListResponse>(`/v1/guestbook${qs({ page, pageSize })}`, { signal }),
    submit: (body: GuestbookSubmitRequest) =>
      request<GuestbookEntry>('/v1/guestbook', { method: 'POST', body }),
  },
  auth: {
    login: (body: { email: string; password: string }) =>
      request<AuthResponse>('/v1/auth/login', { method: 'POST', body, auth: true }),
    refresh: () => request<RefreshResponse>('/v1/auth/refresh', { method: 'POST', auth: true }),
    logout: () =>
      request<{ loggedOut: boolean }>('/v1/auth/logout', { method: 'POST', auth: true }),
    me: () => request<PublicUser>('/v1/auth/me', { bearer: true }),
    changePassword: (body: ChangePasswordRequest) =>
      request<{ changed: boolean }>('/v1/auth/change-password', {
        method: 'POST',
        body,
        auth: true,
        bearer: true,
      }),
  },
  admin: adminApi,
};
